import React, {
  useRef, useState, useEffect, useId,
} from 'react';
import type { MyIpItem } from '$/domains/myIp/types';
import { getMyIpEditDraft, validateMyIpEdit } from '$/domains/myIp/editProfile';
import type { MyIpEditDraft, MyIpEditIssue } from '$/domains/myIp/editProfile';
import { message } from '$/components/Message';
import { useMyIpCopy } from '$/pages/RoboneoMyIps/useMyIpCopy';
import styles from './index.module.scss';

interface Props {
  item: MyIpItem;
  locale: string;
  onSave: (item: MyIpItem, draft: MyIpEditDraft) => Promise<void>;
  onCancel: () => void;
}

/** 编辑草稿与已保存详情隔离；切语言只更新按钮文案，取消和保存失败均保留原语义。 */
export default function MyIpEditForm({
  item, locale, onSave, onCancel,
}: Props) {
  const { edit: copy } = useMyIpCopy();
  const [initial] = useState(() => {
    try { return { draft: getMyIpEditDraft(item), error: false }; } catch { return { draft: undefined, error: true }; }
  });
  const [draft, setDraft] = useState(initial.draft);
  const [issues, setIssues] = useState<MyIpEditIssue[]>([]);
  const [pending, setPending] = useState(false);
  const lock = useRef(false);
  const mounted = useRef(true);
  const form = useRef<HTMLFormElement>(null);
  const formId = useId();
  useEffect(() => {
    mounted.current = true;
    form.current?.querySelector('input')?.focus();
    return () => { mounted.current = false; };
  }, []);

  /** 不支持的旧设定用 message 提示，页面不保留错误条。 */
  useEffect(() => {
    if (initial.error) message.error(copy.unsupported);
  }, [initial.error, copy.unsupported]);

  /** React 状态提交前也阻止双击；异步响应不能影响切换后的 IP。 */
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || lock.current) return;
    const nextIssues = validateMyIpEdit(item, draft, locale);
    setIssues(nextIssues);
    if (nextIssues.length) {
      message.error(nextIssues.map((issue) => copy.limits[issue]).join('；'));
      return;
    }
    lock.current = true;
    setPending(true);
    try {
      await onSave(item, draft);
      if (mounted.current) onCancel();
    } catch {
      if (mounted.current) message.error(copy.saveError);
    } finally {
      lock.current = false;
      if (mounted.current) setPending(false);
    }
  };

  if (initial.error || !draft) { return (
    <section className={styles.editor}>
      <button type="button" onClick={onCancel}>{copy.cancel}</button>
    </section>
  ); }
  const fields = ['name', 'summary', 'persona', 'languageStyle', 'visualStyle'] as const;
  return (
    <form ref={form} className={styles.editor} onSubmit={submit} noValidate aria-label={copy.edit}>
      <h2>{copy.edit}</h2>
      <p className={styles.editHint}>{copy.textOnly}</p>
      <fieldset disabled={pending}>
        {fields.map((field) => (
          <label key={field} className={styles.editField} htmlFor={`${formId}-${field}`}>
            <span id={`${formId}-${field}-label`}>{copy[field]}</span>
            {field === 'name' ? (
              <input id={`${formId}-${field}`} aria-labelledby={`${formId}-${field}-label`} value={draft[field]} required aria-invalid={issues.includes(field)} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })} />
            ) : (
              <textarea id={`${formId}-${field}`} aria-labelledby={`${formId}-${field}-label`} rows={field === 'summary' ? 3 : 5} value={draft[field]} required={field !== 'visualStyle'} aria-invalid={issues.includes(field)} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })} />
            )}
          </label>
        ))}
        <h3>{copy.moreSettings}</h3>
        {draft.moreSettings.map((setting, index) => (
          // 有序 KV 无服务端 ID；输入均受控，删除时只修改本地草稿，不生成持久化 ID。
          // eslint-disable-next-line react/no-array-index-key
          <div className={styles.customSetting} key={index}>
            <label className={styles.editField} htmlFor={`${formId}-key-${index}`}>
              <span>{copy.settingTitle}</span>
              <input id={`${formId}-key-${index}`} value={setting.key} onChange={(event) => setDraft({ ...draft, moreSettings: draft.moreSettings.map((value, i) => (i === index ? { ...value, key: event.target.value } : value)) })} />
            </label>
            <label className={styles.editField} htmlFor={`${formId}-value-${index}`}>
              <span>{copy.settingContent}</span>
              <textarea id={`${formId}-value-${index}`} rows={4} value={setting.value} onChange={(event) => setDraft({ ...draft, moreSettings: draft.moreSettings.map((value, i) => (i === index ? { ...value, value: event.target.value } : value)) })} />
            </label>
            <button className={styles.secondaryButton} type="button" onClick={() => setDraft({ ...draft, moreSettings: draft.moreSettings.filter((_, i) => i !== index) })}>{copy.removeSetting}</button>
          </div>
        ))}
        <button className={styles.secondaryButton} type="button" disabled={draft.moreSettings.length >= 5} onClick={() => setDraft({ ...draft, moreSettings: [...draft.moreSettings, { key: '', value: '' }] })}>{copy.addSetting}</button>
        <div className={styles.editActions}>
          <button type="button" className={styles.secondaryButton} onClick={onCancel}>{copy.cancel}</button>
          <button type="submit" className={styles.primaryButton} aria-busy={pending}>{pending ? copy.saving : copy.save}</button>
        </div>
      </fieldset>
    </form>
  );
}
